'use client';

import { useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';
import type { Project } from '@/shared/lib/types';
import { ProjectRole, ProjectStatus, resolveProjectStatus } from '@/shared/lib/types';

export function useProjectsTable(projects: Project[], onDelete?: (projectId: string) => void) {
  const { data: authSession } = useSession();
  const currentUserEmail = authSession?.user?.email?.toLowerCase() ?? null;

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('');
  const [typeOpen, setTypeOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return projects.filter((project) => {
      const matchesSearch =
        !search ||
        project.name.toLowerCase().includes(term) ||
        project.project_id.toLowerCase().includes(term) ||
        Boolean(project.created_by?.name?.toLowerCase().includes(term)) ||
        Boolean(project.members?.some((member) => member.name?.toLowerCase().includes(term)));
      const status = resolveProjectStatus(project.status, project.is_active);
      const matchesStatus = !statusFilter || status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [projects, search, statusFilter]);

  const isProjectAdmin = (project: Project) => {
    if (!currentUserEmail) return false;
    const isAdmin = project.members?.some(
      (member) =>
        member.email?.toLowerCase() === currentUserEmail &&
        member.role_id === ProjectRole.ADMIN,
    );
    return isAdmin;
  };

  return {
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    typeOpen,
    setTypeOpen,
    statusOpen,
    setStatusOpen,
    filtered,
    isProjectAdmin,
  };
}
